// Comprehensive citation configuration based on APA 7th edition
// This configuration supports category-based citation flow with dynamic fields

export const citationCategories = [
	'Book & Chapter',
	'Periodicals',
	'Web Content',
	'Government & Institutional Publications',
	'Course & Education',
	'Data & Software',
	'Multimedia',
	'Social Media',
	'Personal Communication'
];

// Source order configuration for each category
export const sourceOrder = {
	'Web Content': ['Blog Post', 'Webpage'],
	'Social Media': ['X/Twitter Post'],
	'Personal Communication': ['Personal Communication'],
	Periodicals: [
		'Journal Article',
		'Preprint',
		'Magazine Article (Print & Online)',
		'Newspaper Article (Print & Online)'
	],
	Multimedia: [
		'Film/Movie',
		'TV Series Episode',
		'YouTube Video',
		'Podcast Episode',
		'Webinar/Lecture',
		'Song/Track',
		'Infographic/Chart/Graph',
		'Radio Broadcast'
	],
	'Government & Institutional Publications': ['Institutional Report', 'Press Release'],
	'Data & Software': ['Data set'],
	'Course & Education': [
		'Course Handout',
		'Open Educational Resource',
		'Conference Presentation / Session'
	],
	'Book & Chapter': [
		'Book',
		'Chapter in Edited Book',
		'Reference Work Entry',
		'Thesis/Dissertation'
	]
};

// Complete citation configuration
export const citationConfig = {
	'Web Content': {
		sources: {
			'Blog Post': {
				description:
					"A post published on a blog or online journal, typically written in an informal or conversational tone. Use this source type for posts hosted on blogging platforms (such as Medium, Substack, or an organization's blog page).",
				fields: [
					{
						name: 'AuthorOrOrganization',
						label: 'Author or Organization?',
						type: 'radio',
						required: true,
						options: ['Author(s)', 'Organization'],
						tooltip:
							'Enter the author’s last name followed by initials (e.g., Rivera, J. D.).\nIf the post is from an organization, use the organization\'s full name (e.g., National Public Radio or Harvard Business Review).'
					},
					{
						name: 'author',
						label: 'Author(s)',
						type: 'list',
						required: true,
						showIf: { field: 'AuthorOrOrganization', value: 'Author(s)' },
						tooltip: "Enter the blogger's name (initials + last name)."
					},
					{
						name: 'organization',
						label: 'Organization',
						type: 'text',
						required: true,
						showIf: { field: 'AuthorOrOrganization', value: 'Organization' },
						tooltip:
							'Enter the organization name (e.g., Psychology Today, World Health Organization).'
					},
					{
						name: 'date',
						label: 'Date',
						type: 'date',
						required: true,
						tooltip:
							'Enter the publication or last updated date in year, month, and day format (e.g., 2025, May 8). If no date is shown, enter n.d.'
					},
					{
						name: 'titleOfPost',
						label: 'Title of post',
						type: 'text',
						required: true,
						tooltip:
							'Enter the title exactly as it appears on the page (e.g., COVID-19 prevention resources for schools).'
					},
					{
						name: 'blogName',
						label: 'Blog name',
						type: 'text',
						required: true,
						tooltip:
							'Enter the name of the blog that published the post (e.g., WriteForm Insights or Medium).'
					},
					{
						name: 'url',
						label: 'URL',
						type: 'url',
						required: true,
						tooltip:
							'Enter the full web address of the source (e.g., https://medium.com/example-post).'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip:
							'Add any internal notes or reminders about this source. Notes are not included in the reference.'
					}
				],
				referenceExample:
					'Author, A. A. (Year, Month Day). Title of post. Blog Name. URL\n\nRutledge, P. (2024, April 23). Are tweens too young for digital literacy? Dr. Pam: Living With Media. https://www.pamelarutledge.com/are-tweens-too-young-for-digital-literacy/',
				inTextExample: 'Parenthetical: (Rutledge, 2024)\nNarrative: Rutledge (2024)'
			},
			Webpage: {
				description:
					'A standalone page or article on a website, written by an individual or organization. Use this type for content that is not part of a journal, magazine, or newspaper, for example, university pages, company sites, or professional organizations. Include a retrieval date only if the content is designed to change over time (such as a wiki or live data dashboard).',
				fields: [
					{
						name: 'AuthorOrOrganization',
						label: 'Author or Organization?',
						type: 'radio',
						required: true,
						options: ['Author(s)', 'Organization'],
						tooltip:
							'Enter the author’s last name and initials (e.g., Patel, M. K.).\n If the post is from an organization, use the organization\'s name (e.g., Centers for Disease Control and Prevention).'
					},
					{
						name: 'author',
						label: 'Author(s)',
						type: 'list',
						required: true,
						showIf: { field: 'AuthorOrOrganization', value: 'Author(s)' },
						tooltip: "Enter the author's name (initials + last name)."
					},
					{
						name: 'organization',
						label: 'Organization',
						type: 'text',
						required: true,
						showIf: { field: 'AuthorOrOrganization', value: 'Organization' },
						tooltip:
							'Enter the organization responsible for the page (e.g., Centers for Disease Control and Prevention).'
					},
					{
						name: 'date',
						label: 'Date',
						type: 'date',
						required: true,
						tooltip:
							'Enter the publication or last updated date in year, month, and day format (e.g., 2025, May 8). If no date is shown, enter n.d.'
					},
					{
						name: 'titleOfWebPage',
						label: 'Title of web page',
						type: 'text',
						required: true,
						tooltip:
							'Enter the title exactly as it appears on the page (e.g., COVID-19 prevention resources for schools).'
					},
					{
						name: 'websiteName',
						label: 'Website name',
						type: 'text',
						required: false,
						tooltip:
							'Enter the name of the website that published the page (e.g., CDC.gov or Stanford University).'
					},
					{
						name: 'retrievalDate',
						label: 'Retrieval date',
						type: 'date',
						required: false,
						tooltip:
							'Enter the date you accessed the page (e.g. October 15, 2025). Include only if the content changes over time.'
					},
					{
						name: 'url',
						label: 'URL',
						type: 'url',
						required: true,
						tooltip:
							'Enter the full web address of the source (e.g., https://medium.com/example-post).'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip: 'Add any internal notes or reminders about this source. Notes are not included in the reference.'
					}
				],
				referenceExample:
					'Author, A. A. (Year, Month Day). Title of page. Site Name. URL\n\nWorld Health Organization. (2023, December 8). Cancer. WHO. https://www.who.int/news-room/fact-sheets/detail/cancer',
				inTextExample:
					'Parenthetical: (World Health Organization [WHO], 2023)\nNarrative: World Health Organization (WHO, 2023)'
			}
		}
	},

	'Social Media': {
		sources: {
			'X/Twitter Post': {
				description:
					'A single post shared on X (formerly Twitter), whether text-only, image, video, or a mix of media. Use for individual tweets that are publicly viewable.',
				fields: [
					{
						name: 'publiclyAccessible',
						label: 'Publicly accessible?',
						type: 'radio',
						required: true,
						options: ['Yes', 'No'],
						tooltip:
							"Select 'Yes' if the post can be viewed by anyone on the platform without needing an account. Select 'No' if the post is protected or from a private account, as this may affect your ability to cite it."
					},
					{
						name: 'AuthorOrOrganization',
						label: 'Author or Organization?',
						type: 'radio',
						required: true,
						options: ['Author(s)', 'Organization'],
						tooltip:
							'Enter the author\'s last name followed by initials(e.g., Musk, E.).If the post is from an organization, use the organization\'s name (e.g., NASA or The New York Times).'
					},
					{
						name: 'author',
						label: 'Author(s)',
						type: 'list',
						required: true,
						showIf: { field: 'AuthorOrOrganization', value: 'Author(s)' },
						tooltip:
							'Enter the full name of the person that posted the tweet. Use initials + surname for individuals (e.g., J. D. Rivera).'
					},
					{
						name: 'organization',
						label: 'Organization',
						type: 'text',
						required: true,
						showIf: { field: 'AuthorOrOrganization', value: 'Organization' },
						tooltip:
							'Enter the display name of the organization as it appears (e.g., CDC, Microsoft).'
					},
					{
						name: 'handleUsername',
						label: 'Handle/Username',
						type: 'text',
						required: false,
						tooltip:
							'Enter the author\'s or organization\'s X/Twitter username without the "@" symbol (e.g., NASA, nytimes).'
					},
					{
						name: 'date',
						label: 'Date',
						type: 'date',
						required: true,
						tooltip:
							'Enter the date the post was published in year, month, and day format (e.g., 2024, October 15).'
					},
					{
						name: 'titleOfPost',
						label: 'Title of post',
						type: 'text',
						required: false,
						tooltip:
							'If the post has a clear, distinct title (e.g., from a long-form "X Article"), enter it here. For standard posts, this field is often left blank.'
					},
					{
						name: 'descriptor',
						label: 'Descriptor',
						type: 'select',
						required: true,
						showIf: { field: 'publiclyAccessible', value: 'Yes' },
						options: [
							'Post',
							'Video attached',
							'Image attached',
							'Thumbnail with link attached',
							'Audio attached',
							'Poll attached'
						],
						defaultValue: 'Post',
						tooltip:
							'Use this to add a label if needed for clarity in your citation, such as [Poll], [Video], or [Image]. This is often left blank for standard text posts.'
					},
					{
						name: 'platform',
						label: 'Platform',
						type: 'text',
						required: false,
						defaultValue: 'X',
						disabled: true,
						tooltip:
							"The social media platform is defaulted to X."
					},
					{
						name: 'retrievalDate',
						label: 'Retrieval date',
						type: 'date',
						required: false,
						tooltip:
							'Enter the date you accessed the post in year, month, and day format (e.g., 2025, January 22). This is typically only needed for content that is designed to change over time, like a live video or a running poll, or if the post is not publicly archived.'
					},
					{
						name: 'url',
						label: 'URL',
						type: 'url',
						required: true,
						showIf: { field: 'publiclyAccessible', value: 'Yes' },
						tooltip:
							'Enter the full, permanent web address (URL) of the specific post.'
					},
					// {
					//   name: "timestamp",
					//   label: "Timestamp",
					//   type: "text",
					//   required: false,
					//   tooltip: "Required if user is doing a direct quote from the video, or recording."
					// },
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip: 'Add any internal notes or reminders about this source (e.g., "Screenshot saved," "Part of a longer thread"). Notes are not included in the reference.'
					}
				],
				referenceExample:
					'Author, A. [@Username]. (Year, Month Day). First 20 words of the post. [Descriptor]. Site Name. Retrieved Month Day, Year, from URL\n\nObama, B. [@BarackObama]. (2024, May 17). Today marks 70 years since the Supreme Court handed down its landmark decision in Brown v. Board of Education, outlawing [Post]. X. https://x.com/BarackObama/status/179154059223887495',
				inTextExample:
					'Parenthetical: (World Health Organization [WHO], 2024)\nNarrative: World Health Organization (WHO, 2024)'
			}
		}
	},

	'Personal Communication': {
		sources: {
			'Personal Communication': {
				description:
					'Private, non-recoverable communications such as emails, interviews, phone calls, text messages, or conversations that are not publicly accessible. These sources are cited only in-text and do not appear in the reference list. (APA §8.9)',
				fields: [
					{
						name: 'authorCommunicator',
						label: 'Author/Communicator',
						type: 'text',
						required: true,
						tooltip:
							"Enter the last name and initials of the person who originated the message or provided the information (e.g., Zheng, M.)."
					},
					{
						name: 'recipient',
						label: 'Recipient(s)',
						type: 'text',
						required: false,
						tooltip:
							"Enter the name(s) of the person or group who received the communication (e.g., Garcia, S. and Lee, T.). For a private conversation or interview, you can list yourself (e.g., [Your Name])."
					},
					{
						name: 'date',
						label: 'Date',
						type: 'date',
						required: true,
						tooltip: 'Enter the date the communication occurred or was sent in year, month, and day format (e.g., 2024, November 5).'
					},
					{
						name: 'typeOfCommunication',
						label: 'Type of communication',
						type: 'select',
						required: true,
						options: [
							'Direct message',
							'Email',
							'Internal memo/message',
							'Interview',
							'Lecture (unpublished)',
							'Letter/Mail correspondence',
							'Meeting/Discussion notes',
							'Personal correspondence (unspecified)',
							'Personal interview',
							'Phone call',
							'Private conversation',
							'Private message (chat app)',
							'Text message/SMS',
							'Voicemail',
							'Webinar (unarchived)'
						],
						tooltip:
							'Select the specific method of exchange from the dropdown list. This categorizes the private source (e.g., Email, Personal interview, Text message).'
					},
					{
						name: 'medium',
						label: 'Medium',
						type: 'text',
						required: false,
						tooltip:
							'Specify the platform or method used, if it provides clarifying detail not captured in the type (e.g., for "Private message," you could note "Slack"; for "Phone call," you could note "Zoom").'
					},
					{
						name: 'institutionContext',
						label: 'Institution/Context',
						type: 'text',
						required: false,
						tooltip:
							'Note the relevant professional, academic, or organizational context for the communication (e.g., "Marketing Department meeting," "Research interview for Project Alpha").'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip:
							'Add any internal notes or reminders (e.g., "Summary of key points attached," "Verbal permission granted to cite"). Notes are not included in the reference.'
					}
				],
				referenceExample:
					'Personal communications are cited in-text only and do not appear in the reference list.',
				inTextExample:
					'Parenthetical: (C. A. Hill, personal communication, March 5, 2025)\nNarrative: According to C. A. Hill (personal communication, March 5, 2025)...'
			}
		}
	},

	Periodicals: {
		sources: {
			'Journal Article': {
				description:
					'A scholarly paper published in a peer-reviewed or academic journal. Includes research studies, reviews, or theoretical articles available in print or online with a DOI or journal homepage URL.',
				fields: [
					{
						name: 'AuthorOrOrganization',
						label: 'Author or Organization?',
						type: 'radio',
						required: true,
						options: ['Author(s)', 'Organization'],
						tooltip:
							'Enter each author’s full name (e.g., Stephen King). List all authors in the order they appear on the title page. If no individual author is listed, enter the organization name(e.g., World Health Organization).'
					},
					{
						name: 'author',
						label: 'Author(s)',
						type: 'list',
						required: true,
						showIf: { field: 'AuthorOrOrganization', value: 'Author(s)' },
						tooltip: "Enter each author’s full name. List authors in the order they appear in the publication."
					},
					{
						name: 'organization',
						label: 'Organization',
						type: 'text',
						required: true,
						showIf: { field: 'AuthorOrOrganization', value: 'Organization' },
						tooltip:
							'Enter the organization responsible for the paper (e.g., American Psychological Association).'
					},
					{
						name: 'year',
						label: 'Year',
						type: 'text',
						required: true,
						tooltip:
							'Enter the year (2023), or a fuller date in the reference list such as 2023, January or 2023, May 16. Use n.d. if there is no date. You may also enter nd — it will display as n.d.'
					},
					{
						name: 'titleOfArticle',
						label: 'Title of article',
						type: 'text',
						required: true,
						tooltip:
							'Enter the title of the article exactly as it appears. Do not italicize or add quotation marks in this field.'
					},
					{
						name: 'titleOfJournal',
						label: 'Title of journal',
						type: 'text',
						required: true,
						tooltip:
							'Enter the full name of the journal as it appears on the source, in title case (e.g., Journal of Applied Psychology).'
					},
					{
						name: 'volume',
						label: 'Volume',
						type: 'text',
						required: true,
						tooltip:
							'Enter the volume number of the journal. This is often associated with the year of publication.'
					},
					{
						name: 'issue',
						label: 'Issue',
						type: 'text',
						required: false,
						tooltip:
							'Enter the issue number of the journal, if available. This is usually found within a volume (e.g., Volume 12, Issue 3).'
					},
					{
						name: 'pageRangeStart',
						label: 'Page range - Start',
						type: 'text',
						required: true,
						width: '50%',
						tooltip:
							'Enter the starting page number (e.g., 15). For single-page articles, enter the same number in both start and end.'
					},
					{
						name: 'pageRangeEnd',
						label: 'Page range - End',
						type: 'text',
						required: true,
						width: '50%',
						tooltip:
							'Enter the ending page number (e.g., 18). For single-page articles, enter the same number in both start and end.'
					},
					{
						name: 'doiUrl',
						label: 'DOI/URL',
						type: 'url',
						required: true,
						tooltip:
							'Enter the Digital Object Identifier (DOI) if available (e.g., 10.1037/amp0001234). If there is no DOI, provide a stable, permanent URL to the article on the journal\'s website.'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip: 'Add any internal notes or reminders (e.g., "Focus on methodology," "Used in literature review"). Notes are not included in the reference.'
					}
				],
				referenceExample:
					"Author, A. A., & Author, B. B. (Year). Title of article. Journal Title, Volume(Issue), page–page. https://doi.org/xxxx\n\nNiepel, C., Hausen, J. E., Weber, A. M., & Möller, J. (2025). Understanding mean-level and intraindividual variability in state academic self-concept: The role of students' trait expectancies and values. Journal of Educational Psychology, 117(5), 772–788. https://doi.org/10.1037/edu0000946",
				inTextExample: 'Parenthetical: (Niepel et al., 2025)\nNarrative: Niepel et al. (2025)'
			},
			'Magazine Article (Print & Online)': {
				description:
					'An article written for a general or specialized audience and published in a print or online magazine. Often provides commentary, interviews, or feature stories rather than peer-reviewed research.',
				fields: [
					{
						name: 'AuthorOrOrganization',
						label: 'Author or Organization?',
						type: 'radio',
						required: false,
						options: ['Author(s)', 'Organization'],
						tooltip:
							'Enter each author’s full name (e.g., Stephen King). List all authors in the order they appear on the title page.If no individual author is listed, enter the organization name or the magazine\'s editorial board (e.g., The Economist or Scientific American).'
					},
					{
						name: 'author',
						label: 'Author(s)',
						type: 'list',
						required: false,
						showIf: { field: 'AuthorOrOrganization', value: 'Author(s)' },
						tooltip: "Enter the author's name (initials + last name)."
					},
					{
						name: 'organization',
						label: 'Organization',
						type: 'text',
						required: false,
						showIf: { field: 'AuthorOrOrganization', value: 'Organization' },
						tooltip:
							'Enter the organization responsible for the article (e.g., American Psychological Association).'
					},
					{
						name: 'date',
						label: 'Date',
						type: 'date',
						required: true,
						tooltip:
							'Enter the publication date in year, month, and day format (e.g., 2026, January 15). You may also enter year only, year and month, or n.d. if no date is shown.'
					},
					{
						name: 'publishedOnline',
						label: 'Published online?',
						type: 'select',
						required: true,
						options: ['Yes', 'No'],
						tooltip:
							'Select how you accessed the article. Print articles use page ranges. Online articles use a URL and usually do not use page ranges.'
					},
					{
						name: 'titleOfArticle',
						label: 'Title of article',
						type: 'text',
						required: true,
						tooltip:
							'Enter the title of the article exactly as it appears in the magazine.'
					},
					{
						name: 'titleOfMagazine',
						label: 'Title of magazine',
						type: 'text',
						required: true,
						tooltip:
							'Enter the full name of the magazine in title case, WriteForm will italicize it (e.g., The New Yorker).'
					},
					{
						name: 'volume',
						label: 'Volume',
						type: 'text',
						required: false,
						tooltip:
							'Enter the volume number of the magazine, if provided. This is more common in academic or trade magazines.'
					},
					{
						name: 'issue',
						label: 'Issue',
						type: 'text',
						required: false,
						tooltip:
							'Enter the issue number of the magazine (e.g., Issue 5), if provided. For weekly magazines, the date is often sufficient.'
					},
					{
						name: 'pageRangeStart',
						label: 'Page range - Start',
						type: 'text',
						required: false,
						width: '50%',
						tooltip:
							'Enter the starting page number (e.g., 12). Leave blank for online-only pieces.'
					},
					{
						name: 'pageRangeEnd',
						label: 'Page range - End',
						type: 'text',
						required: false,
						width: '50%',
						tooltip: 'Enter the ending page number (e.g., 17). Leave blank for online-only pieces.'
					},
					{
						name: 'doiUrl',
						label: 'DOI/URL',
						type: 'url',
						required: false,
						showIf: { field: 'publishedOnline', value: 'Yes' },
						tooltip:
							'Enter the stable URL (web address) linking directly to the article on the magazine\'s website.If a DOI is assigned, use that instead(e.g., 10.1038/scientificamerican1124-50).'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip: 'Add any internal notes or reminders about this source. Notes are not included in the reference.'
					}
				],
				referenceExample:
					'Nicholl, K. (2020, May 3). A royal spark. Vanity Fair. https://www.vanityfair.com/style/2020/05/a-royal-spark\n\nPrint with Volume & Issue:\nNicholl, K. (2020, May). A royal spark. Vanity Fair, 62(5), 56–65, 100.\n\nNo Author Example:\nThe future of AI in education. (2025, March). Technology Review. https://www.technologyreview.com/ai-education',
				inTextExample:
					'Parenthetical: (Nicholl, 2020)\nNarrative: Nicholl (2020)\n\nNo Author: ("The Future of AI," 2025)'
			},
			'Newspaper Article (Print & Online)': {
				description:
					'A news story, feature, or opinion piece published in a print or online newspaper. Typically provides current events, commentary, or local coverage and may include an online retrieval URL.',
				fields: [
					{
						name: 'AuthorOrOrganization',
						label: 'Author or Organization?',
						type: 'radio',
						required: false,
						options: ['Author(s)', 'Organization'],
						tooltip:
							'Enter the author\'s last name followed by initials (e.g., Smith, J.A.). If no individual author is listed, use the organization name or the newspaper\'s editorial board (e.g., The Washington Post or Reuters).'
					},
					{
						name: 'author',
						label: 'Author(s)',
						type: 'list',
						required: false,
						showIf: { field: 'AuthorOrOrganization', value: 'Author(s)' },
						tooltip: "Enter the author's name (initials + last name)."
					},
					{
						name: 'organization',
						label: 'Organization',
						type: 'text',
						required: false,
						showIf: { field: 'AuthorOrOrganization', value: 'Organization' },
						tooltip: 'Enter the organization responsible for the article (e.g., Associated Press).'
					},
					{
						name: 'date',
						label: 'Date',
						type: 'date',
						required: true,
						tooltip:
							'Enter the publication date in year, month, and day format (e.g., 2024, June 3), or n.d. if no date is shown.'
					},
					{
						name: 'publishedOnline',
						label: 'Published online?',
						type: 'select',
						required: true,
						options: ['Yes', 'No'],
						tooltip:
							'Select how you accessed the article. Print articles use page ranges. Online articles use a URL and usually do not use page ranges.'
					},
					{
						name: 'titleOfArticle',
						label: 'Title of article',
						type: 'text',
						required: true,
						tooltip:
							'Enter the title of the article exactly as it appears in the newspaper.'
					},
					{
						name: 'titleOfNewspaper',
						label: 'Title of newspaper',
						type: 'text',
						required: true,
						tooltip:
							'Enter the full name of the newspaper in title case, WriteForm will italicize it (e.g., The Boston Globe).'
					},
					{
						name: 'pageNumbersStart',
						label: 'Starting page',
						type: 'text',
						required: false,
						tooltip:
							'Enter the starting page number, including section letters if listed (e.g., A1). Leave blank for online articles.'
					},
					{
						name: 'pageNumbersEnd',
						label: 'Ending page',
						type: 'text',
						required: false,
						tooltip:
							'Enter the ending page number, including section letters if listed (e.g., A5). Leave blank for online articles.'
					},
					{
						name: 'doiUrl',
						label: 'DOI/URL',
						type: 'url',
						required: false,
						showIf: { field: 'publishedOnline', value: 'Yes' },
						tooltip:
							"Enter the stable URL (web address) linking directly to the article on the newspaper's website. If a DOI is assigned (rare for newspapers), use that instead. Required for online articles."
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip: 'Add any internal notes or reminders about this source (e.g., "Front page story," "Includes important chart"). Notes are not included in the reference.'
					}
				],
				referenceExample:
					"Bernstein, J. (2024, June 3). The man who couldn't stop going to college. The New York Times. https://www.nytimes.com/2024/06/03/magazine/benjamin-bolger-college-harvard-yale.htm\n\nNo Author Example:\nGraduate writing centers turn to AI-powered support. (2025, July 10). The Chronicle of Higher Education. https://www.chronicle.com/article/ai-writing-centers",
				inTextExample:
					'Parenthetical: (Bernstein, 2024)\nNarrative: Bernstein (2024)\n\nNo Author: ("Graduate Writing Centers," 2025)'
			},
			Preprint: {
				description:
					'A preprint is a manuscript or article posted online before formal peer-review or publication in a journal. It may later appear in a journal, but the preprint is a stand-alone, citable version in a public repository (e.g., PsyArXiv, arXiv, medRxiv, SocArXiv).',
				fields: [
					{
						name: 'AuthorOrOrganization',
						label: 'Author or Organization?',
						type: 'radio',
						required: true,
						options: ['Author(s)', 'Organization'],
						tooltip:
							'Enter each author’s full name (e.g., Stephen King). List all authors in the order they appear on the title page.If no individual author is listed, enter the organization name.'
					},
					{
						name: 'author',
						label: 'Author(s)',
						type: 'list',
						required: true,
						showIf: { field: 'AuthorOrOrganization', value: 'Author(s)' },
						tooltip: "Enter the author's name (initials + last name)."
					},
					{
						name: 'organization',
						label: 'Organization',
						type: 'text',
						required: true,
						showIf: { field: 'AuthorOrOrganization', value: 'Organization' },
						tooltip:
							'Enter the organization that posted or authored the preprint (e.g., World Health Organization).'
					},
					{
						name: 'year',
						label: 'Year',
						type: 'text',
						required: true,
						tooltip:
							'Enter the year the preprint was posted to the repository (e.g., 2024).'
					},
					{
						name: 'titleOfManuscript',
						label: 'Title of manuscript',
						type: 'text',
						required: true,
						tooltip:
							'Enter the title of the preprint manuscript exactly as it appears on the repository website.'
					},
					{
						name: 'version',
						label: 'Version',
						type: 'text',
						required: false,
						tooltip:
							'Enter the specific version number of the preprint, if provided (e.g., Version 2). Preprints are often updated, so noting the version is crucial.'
					},
					{
						name: 'descriptor',
						label: 'Descriptor',
						type: 'text',
						required: false,
						defaultValue: 'Preprint',
						disabled: true,
						tooltip:
							'WriteForm automatically inserts “[Preprint]” after the title, if the repository name does not already imply preprint status.'
					},
					{
						name: 'repositoryArchiveName',
						label: 'Repository/Archive name',
						type: 'text',
						required: true,
						tooltip:
							'Enter the name of the preprint server or archive where the manuscript is hosted (e.g., arXiv, bioRxiv, PsyArXiv, SSRN).'
					},
					{
						name: 'publishedOnline',
						label: 'Published online?',
						type: 'select',
						required: true,
						options: ['Yes', 'No'],
						tooltip:
							'Most preprints are published online. Select Yes unless citing an unusual print version.'
					},
					{
						name: 'doiUrl',
						label: 'DOI/URL',
						type: 'url',
						required: false,
						showIf: { field: 'publishedOnline', value: 'Yes' },
						tooltip:
							'Enter the Digital Object Identifier (DOI) or the stable URL linking directly to the specific version of the preprint. Required when Published online is Yes.'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip: 'Add any internal notes or reminders (e.g., "Under review at Nature," "Check for updated version"). Notes are not included in the reference.'
					}
				],
				referenceExample:
					'Latimier, A., Peyre, H., & Ramus, F. (2020). A meta-analytic review of the benefit of spacing out retrieval practice episodes on retention [Preprint]. PsyArXiv. https://psyarxiv.com/kzy7u\n\nWith Version:\nRivera, J. D., & Hill, C. A. (2025). AI-assisted formatting in academic writing tools (Version 2) [Preprint]. PsyArXiv. https://doi.org/10.31234/osf.io/7xp29',
				inTextExample: 'Parenthetical: (Latimier et al., 2020)\nNarrative: Latimier et al. (2020)'
			}
		}
	},

	Multimedia: {
		sources: {
			'Film/Movie': {
				description:
					'A motion picture released theatrically or via streaming/home media. Typically credited to the director (and producers), dated by release year, and associated with the production/distribution company.',
				fields: [
					{
						name: 'contributors',
						label: 'Creator(s)/Organization',
						type: 'list',
						required: true,
						tooltip:
							'Enter the primary creator\'s last name followed by initials(e.g., Nolan, C.).If the work is created by an organization(e.g., PBS, Netflix), enter the organization\'s name here. Note: If you enter an Organization, it does not receive a "role" in the Contributor field below.'
					},
					{
						name: 'contributorRoles',
						label: 'Contributor Role',
						type: 'select-list',
						required: true,
						options: ['Director', 'Producer', 'Writer', 'Organization (no role)'],
						tooltip:
							'Select the primary role of the person listed in the "Creator(s) / Organization" field. If you entered an Organization name, you can skip this field or select "Organization (no role)".'
					},
					{
						name: 'date',
						label: 'Date',
						type: 'date',
						required: true,
						tooltip: 'Enter the year of release or publication in year, month, and day format if available (e.g., 2020, or 2020, July 16)'
					},
					{
						name: 'titleOfWork',
						label: 'Title of work',
						type: 'text',
						required: true,
						tooltip:
							'Enter the title of the film, video, or episode. WriteForm will italicize the title (e.g., The Social Dilemma or The One with the Embryos).'
					},
					{
						name: 'descriptor',
						label: 'Descriptor',
						type: 'text',
						required: true,
						defaultValue: 'Film',
						disabled: true,
						tooltip:
							'This field is auto-generated based on your selected source type. WriteForm will display [Film] after the title'
					},
					{
						name: 'productionCompany',
						label: 'Production company/Network',
						type: 'text',
						required: false,
						tooltip: 'Enter the name of the studio, production company, or network that produced the work (e.g., Marvel Studios, HBO, TED).'
					},
					{
						name: 'platform',
						label: 'Platform',
						type: 'text',
						required: false,
						tooltip: 'If you accessed the work via a streaming service, enter its name here (e.g., Netflix, Disney+, YouTube).'
					},
					{
						name: 'url',
						label: 'URL',
						type: 'url',
						required: false,
						tooltip: 'For online videos, enter the full web address where the work can be streamed.'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip: 'Add any internal notes or reminders (e.g., "Cited the director," "Timestamp 30:15"). Notes are not included in the reference.'
					}
				],
				referenceExample:
					"Director's Last Name, Initials. (Director). (Year). Title of film [Film]. Production Company.\n\nSpielberg, S. (Director). (2018). Ready player one [Film]. Warner Bros.",
				inTextExample:
					'Parenthetical: (Spielberg, 1993)\nNarrative: Spielberg (1993)\nFor a direct quote, include a timestamp: (Spielberg, 1993, 1:15:35)'
			},
			'YouTube Video': {
				description:
					'A single, publicly viewable video hosted on YouTube. Credited to the channel/uploader with an exact upload date and a stable watch URL.',
				fields: [
					{
						name: 'publiclyAccessible',
						label: 'Publicly Accessible?',
						type: 'radio',
						required: true,
						options: ['Yes', 'No'],
						tooltip:
							'Select "Yes" if the video can be viewed by anyone without signing into a YouTube account. Select "No" if the video is unlisted, private, or requires membership to view, as this may affect your ability to cite it.'
					},
					{
						name: 'authorUploader',
						label: 'Author(s)/Uploader',
						type: 'list',
						required: true,
						maxItems: 1,
						tooltip:
							'Enter the real name of the individual or the full name of the organization that created or uploaded the video (e.g., National Geographic, Csikszentmihalyi, M.). If only a channel name is available, use that. Only one uploader is allowed per video.'
					},
					{
						name: 'handleUsername',
						label: 'User name',
						type: 'text',
						required: false,
						tooltip: 'Enter the channel or user name as shown on YouTube, without the @ symbol (e.g., TED, kurzgesagt).'
					},
					{
						name: 'date',
						label: 'Date',
						type: 'date',
						required: true,
						tooltip: 'Enter the date the video was published on YouTube in year, month, and day format (e.g., 2023, October 5).'
					},
					{
						name: 'titleOfVideo',
						label: 'Title of video',
						type: 'text',
						required: true,
						tooltip:
							'Enter the title of the video exactly as it appears on the YouTube page.'
					},
					{
						name: 'descriptor',
						label: 'Descriptor',
						type: 'text',
						required: false,
						defaultValue: 'Video',
						disabled: true,
						tooltip:
							'This field is auto-generated based on your selected source type. WriteForm will display [Video] after the title.'
					},
					{
						name: 'platformName',
						label: 'Platform name',
						type: 'text',
						required: false,
						defaultValue: 'YouTube',
						disabled: true,
						tooltip: 'WriteForm will automatically set this to YouTube.'
					},
					{
						name: 'url',
						label: 'URL',
						type: 'url',
						required: true,
						tooltip: 'Enter the full, permanent web address (URL) of the specific video.'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip: 'Add any internal notes or reminders about this source (e.g., "Screenshot of graph at 5:30," "Creator is a recognized expert"). Notes are not included in the reference.'
					}
				],
				referenceExample:
					'Author, A. A. [Channel Name]. (Year, Month Day). Title of work [Video]. YouTube. URL\n\nAPA Style. (2020, March 17). How to cite YouTube videos in APA style (7th edition) [Video]. YouTube. https://www.youtube.com/watch?v=ZXKztO7i6J0',
				inTextExample:
					'Parenthetical: (APA Style, 2020)\nNarrative: APA Style (2020)\nFor a direct quote: (APA Style, 2020, 2:15)'
			},
			'TV Series Episode': {
				description:
					'A single installment of a television series (broadcast, cable, or streaming). Credited to writer(s)/director, dated by original air/stream date, and linked to its parent series.',
				fields: [
					{
						name: 'writers',
						label: 'Writer(s)',
						type: 'list',
						required: true,
						tooltip:
							'Enter the last name and initials of the primary writer(s) for this specific episode (e.g., Korsh, A.).'
					},
					{
						name: 'directors',
						label: 'Director(s)',
						type: 'list',
						required: true,
						tooltip:
							'Enter the last name and initials of the director(s) for this specific episode (e.g., Silverstein, S.).'
					},
					{
						name: 'executiveProducers',
						label: 'Executive Producer(s)',
						type: 'list',
						required: true,
						tooltip:
							'Enter the last name and initials of the show\'s key executive producer(s) (e.g., Lorre, C.). These are typically the creators or showrunners of the entire series.'
					},
					{
						name: 'date',
						label: 'Date',
						type: 'date',
						required: true,
						tooltip:
							'Enter the original broadcast or release date in year, month, and day format (e.g., 2023, September 19). Use n.d. if unknown.'
					},
					{
						name: 'titleOfEpisode',
						label: 'Title of episode',
						type: 'text',
						required: true,
						tooltip:
							'Enter the title of the specific episode. Do not italicize or use quotation marks in this field.'
					},
					{
						name: 'season',
						label: 'Season',
						type: 'text',
						required: false,
						tooltip:
							'Enter the season number (e.g., 2). For shows without formal seasons, you may note the series or volume.'
					},
					{
						name: 'episodeNumber',
						label: 'Episode number',
						type: 'text',
						required: false,
						tooltip:
							'Enter the number of the episode within the season (e.g., 4).'
					},
					{
						name: 'descriptor',
						label: 'Descriptor',
						type: 'text',
						required: false,
						defaultValue: 'TV series episode',
						disabled: true,
						tooltip:
							'This field is auto-generated based on your selected source type. WriteForm will display [TV series episode] after the title'
					},
					{
						name: 'titleOfSeries',
						label: 'Title of series',
						type: 'text',
						required: true,
						tooltip:
							"Enter the title of the entire TV series in title case, WriteForm will italicize it (e.g., Succession)."
					},
					{
						name: 'productionCompany',
						label: 'Production company/Network',
						type: 'text',
						required: true,
						tooltip:
							'Enter the name of the network that originally aired the series or the production company that produced it (e.g., HBO, Netflix, BBC).'
					},
					{
						name: 'platform',
						label: 'Platform',
						type: 'text',
						required: false,
						tooltip:
							'If you accessed the episode via a streaming service, enter its name here (e.g., Hulu, Disney+). If you watched it on the original network, this may be the same as the production company/network.'
					},
					{
						name: 'url',
						label: 'URL',
						type: 'url',
						required: false,
						tooltip:
							'For episodes streamed online, enter the full web address where the episode can be viewed.'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip: 'Add any internal notes or reminders (e.g., "Series finale," "Important plot point for analysis"). Notes are not included in the reference.'
					}
				],
				referenceExample:
					'Writer, A. A. (Writer), & Director, B. B. (Director). (Year, Month Day). Title of episode (Season Number, Episode Number) [TV series episode]. In A. A. Executive Producer (Executive Producer), Title of series. Production Company.',
				inTextExample:
					'Paraphrasing: (Writer & Director, Year)\nExample: (Favreau & Filoni, 2019)\nDirect Quote: (Writer & Director, Year, Timestamp)\nExample: (Favreau & Filoni, 2019, 12:45)'
			},
			'Podcast Episode': {
				description:
					'A single podcast episode available as audio or video through a streaming platform or podcast website. Each episode has its own title, date, and URL — separate from the overall podcast series.',
				fields: [
					{
						name: 'contributors',
						label: 'Contributor(s)/Organization',
						type: 'list',
						required: true,
						tooltip:
							'Enter the last name and initials of the primary contributor (e.g., Gladwell, M.). If the podcast is produced by an organization without highlighting specific individuals, enter the organization\'s name here.'
					},
					{
						name: 'contributorRoles',
						label: 'Contributor role',
						type: 'select-list',
						required: true,
						options: [
							'Host',
							'Guest',
							'Producer',
							'Writer',
							'Director',
							'Narrator',
							'Organization (no role assigned)'
						],
						tooltip:
							"Select the most appropriate role that describes the contributor's primary function in the episode. If you entered an Organization name, select 'Organization (no role assigned)'."
					},
					{
						name: 'date',
						label: 'Date',
						type: 'date',
						required: true,
						tooltip:
							'Enter the date the episode was published in year, month, and day format (e.g., 2024, June 11).'
					},
					{
						name: 'titleOfEpisode',
						label: 'Title of episode',
						type: 'text',
						required: true,
						tooltip:
							'Enter the title of the specific episode. Do not italicize or use quotation marks in this field.'
					},
					{
						name: 'episodeNumber',
						label: 'Episode number',
						type: 'text',
						required: false,
						tooltip:
							'Enter the episode number, if available (e.g., 145).'
					},
					{
						name: 'descriptor',
						label: 'Descriptor',
						type: 'select',
						required: true,
						options: ['Audio podcast episode', 'Video podcast episode'],
						tooltip:
							'Select the option that best describes the format of the podcast you are citing.'
					},
					{
						name: 'titleOfPodcast',
						label: 'Title of podcast',
						type: 'text',
						required: true,
						tooltip:
							'Enter the title of the entire podcast series in title case, WriteForm will italicize it (e.g., Stuff You Should Know).'
					},
					{
						name: 'publisherNetwork',
						label: 'Publisher/Network',
						type: 'text',
						required: false,
						tooltip:
							'Enter the name of the organization or platform that produces or distributes the podcast (e.g., iHeartRadio, Gimlet Media, Wondery).'
					},
					{
						name: 'url',
						label: 'URL',
						type: 'url',
						required: true,
						tooltip: 'Enter the full, stable web address where you accessed or streamed the episode.'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip:
							'Add any internal notes or reminders (e.g., "Cited segment starting at 15:30," "Guest is an industry expert"). Notes are not included in the reference.'
					}
				],
				referenceExample:
					'Author (Role). (Date). Title of episode (No. X) [Audio/Video podcast episode]. In Series title. Publisher/Production Company. URL',
				inTextExample: 'Parenthetical: (Author, Year)\nNarrative: Author (Year)'
			},
			'Webinar/Lecture': {
				description:
					'A recorded online lecture, webinar, or classroom session that can be replayed through a stable, public link (e.g., YouTube, university site, or learning platform). Use this for recorded presentations that are retrievable by others, not for live or internal classroom sessions.',
				fields: [
					{
						name: 'publiclyAccessible',
						label: 'Publicly accessible?',
						type: 'radio',
						required: true,
						options: ['Yes', 'No'],
						tooltip:
							"Select 'Yes' if the recording can be viewed by anyone without special access (e.g., on a public YouTube channel or website). Select 'No' if it is behind a login or restricted to a specific group(e.g., a company intranet or a paid course platform)."
					},
					{
						name: 'presentersLecturers',
						label: 'Presenter(s)/Lecturer(s)',
						type: 'list',
						required: true,
						tooltip: "Enter the last name and initials of the primary speaker(s) (e.g., Ngo, T. L.)."
					},
					{
						name: 'date',
						label: 'Date',
						type: 'date',
						required: true,
						tooltip:
							'Enter the date the event was originally presented or recorded in year, month, and day format (e.g., 2024, March 12).'
					},
					{
						name: 'titleOfRecording',
						label: 'Title of recording',
						type: 'text',
						required: true,
						tooltip:
							'Enter the title of the specific webinar or lecture as it appears on the platform.'
					},
					{
						name: 'descriptor',
						label: 'Descriptor',
						type: 'select',
						required: true,
						options: ['Webinar', 'Lecture recording'],
						tooltip:
							'Select the option that best categorizes the source from the dropdown.'
					},
					{
						name: 'eventInstitution',
						label: 'Event/Institution',
						type: 'text',
						required: false,
						tooltip:
							'Enter the name of the conference, course, or organization that hosted the event (e.g., "UX Design Conference 2024," "Stanford University CS106 Course").'
					},
					{
						name: 'platform',
						label: 'Platform',
						type: 'text',
						required: true,
						tooltip:
							'Enter the name of the website or service where you accessed the recording (e.g., YouTube, Vimeo, Coursera, company intranet).'
					},
					{
						name: 'doiUrl',
						label: 'DOI/URL',
						type: 'url',
						required: true,
						tooltip:
							'Enter the stable URL or Digital Object Identifier (DOI) that links directly to the recording.'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip:
							'Add any internal notes or reminders (e.g., "Key point about methodology at 10:15"). Notes are not included in the reference.'
					}
				],
				referenceExample:
					'Presenter(s). (Year, Month Day). Title of webinar or lecture [Webinar] [OR] [Lecture recording]. Platform/Host. URL\n\nLee, C., McAdoo, T., & Denneny, S. (2023). APA Style seventh edition refresher for instructors [Webinar]. American Psychological Association. https://apastyle.apa.org/instructional-aids/tutorials-webinars',
				inTextExample: 'Narrative: Smith (2024)\nParenthetical: (Smith, 2024)'
			},
			'Song/Track': {
				description:
					'A single audio work issued on its own or as part of an album/EP. Credit the performing artist (and songwriter if known), the release date, and the parent album if applicable.',
				fields: [
					{
						name: 'publiclyAccessible',
						label: 'Publicly Accessible?',
						type: 'radio',
						required: true,
						options: ['Yes', 'No'],
						tooltip:
							'Select "Yes" if the track is available to the general public on major platforms. Select "No" if it is a private, unreleased, or hard- to - find recording, as this may affect your ability to cite it.'
					},
					{
						name: 'accessMethod',
						label: 'How did you access this song/track?',
						type: 'select',
						required: true,
						options: [
							'Streaming service (Spotify, Apple Music, etc.)',
							'Physical media (CD, vinyl, cassette)',
							'Downloaded file (MP3, etc.)'
						],
						tooltip:
							'Select the method you used to listen to the track, as this determines the correct citation format.'
					},
					{
						name: 'artistGroup',
						label: 'Artist(s)/Group',
						type: 'list',
						required: true,
						tooltip:
							'Enter the last name and initials or the stage name of the primary recording artist(s) or group (e.g., Swift, T., or The Beatles).'
					},
					{
						name: 'year',
						label: 'Year',
						type: 'text',
						required: true,
						tooltip:
							'Enter the year the specific track was released or the year of the album\'s release(e.g., 2020).'
					},
					{
						name: 'titleOfSong',
						label: 'Title of song/track',
						type: 'text',
						required: true,
						tooltip:
							'Enter the title of the specific song or track. Do not italicize or use quotation marks in this field.'
					},
					{
						name: 'descriptor',
						label: 'Descriptor',
						type: 'text',
						required: true,
						defaultValue: 'Song',
						disabled: true,
						tooltip:
							'This field is auto-generated based on your selected source type. WriteForm will display [Song] after the title.'
					},
					{
						name: 'titleOfAlbum',
						label: 'Title of album',
						type: 'text',
						required: true,
						tooltip:
							'Enter the title of the album that the track appears on, WriteForm will italicize it (e.g., Folklore). If it\'s a single, enter the title of the single here.'
					},
					{
						name: 'recordLabel',
						label: 'Record label/Publisher',
						type: 'text',
						required: false,
						tooltip:
							'Enter the name of the company that produced or distributed the recording (e.g., Republic Records, Penguin Audio).'
					},
					{
						name: 'platform',
						label: 'Platform',
						type: 'text',
						required: false,
						tooltip:
							'If you accessed the track via a streaming service, enter its name here (e.g., Spotify, Apple Music). For physical media, you can note the format (e.g., CD).'
					},
					{
						name: 'url',
						label: 'URL',
						type: 'url',
						required: false,
						tooltip:
							'For streaming services or downloaded files from a website, enter the stable URL where the track can be accessed or purchased.'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip: 'Add any internal notes or reminders (e.g., "Cited lyrics from first verse," "Sample of another song included"). Notes are not included in the reference.'
					}
				],
				referenceExample:
					'Artist, A. A. (Year). Title of song [Song]. On Title of album. Record Label. Platform. URL',
				inTextExample: 'Parenthetical: (Artist, Year)\nNarrative: Artist (Year)'
			},
			'Infographic/Chart/Graph': {
				description:
					'A data visualization (e.g., bar chart, line graph, infographic) published as a standalone item in print or online. Credit the creator/publisher and provide the page/URL; use a retrieval date for changeable web pages.',
				fields: [
					{
						name: 'AuthorOrOrganization',
						label: 'Author or Organization?',
						type: 'radio',
						required: true,
						options: ['Author(s)', 'Organization'],
						tooltip:
							'Enter the last name and initials of the individual who created the visual (e.g., Lee, S.). If created by an organization, enter the organization\'s name(e.g., Pew Research Center, CDC).'
					},
					{
						name: 'author',
						label: 'Author(s)',
						type: 'list',
						required: true,
						showIf: { field: 'AuthorOrOrganization', value: 'Author(s)' },
						tooltip:
							'Enter the person that created the visual (e.g., J. K. Patel). Use initials + surname.'
					},
					{
						name: 'organization',
						label: 'Organization',
						type: 'text',
						required: true,
						showIf: { field: 'AuthorOrOrganization', value: 'Organization' },
						tooltip:
							'Enter the organization that created the visual (e.g., World Bank, Pew Research Center).'
					},
					{
						name: 'date',
						label: 'Date',
						type: 'date',
						required: true,
						tooltip:
							'Enter the date of publication in year, month, and day format (e.g., 2023, August 10). If no date is shown, enter n.d.'
					},
					{
						name: 'titleOfWork',
						label: 'Title of work',
						type: 'text',
						required: true,
						tooltip:
							'Enter the title or a descriptive title of the visual as it appears on the source (e.g., "Global Internet Usage Growth 2010-2023"). If untitled, create a concise description in sentence case.'
					},
					{
						name: 'descriptor',
						label: 'Descriptor',
						type: 'select',
						required: true,
						options: ['Chart', 'Graph', 'Infographic', 'Data visualization'],
						tooltip:
							'Select the option that best describes the type of visual you are citing.'
					},
					{
						name: 'platformPublisher',
						label: 'Platform/Publisher',
						type: 'text',
						required: false,
						tooltip:
							'Enter the name of the website, publication, or institution that published the visual (e.g., National Geographic, Datawrapper, U.S. Bureau of Labor Statistics).'
					},
					{
						name: 'retrievalDate',
						label: 'Retrieval date',
						type: 'date',
						required: false,
						tooltip:
							'Enter the date you accessed the source in year, month, and day format. This is typically only needed for content that is regularly updated or is part of a dynamic dataset.'
					},
					{
						name: 'doiUrl',
						label: 'DOI/URL',
						type: 'url',
						required: true,
						tooltip:
							'Enter the stable URL or Digital Object Identifier (DOI) that links directly to the visual on the publisher\'s website.'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip: 'Add any internal notes or reminders (e.g., "Key statistic used in introduction," "Part of a larger annual report"). Notes are not included in the reference.'
					}
				],
				referenceExample:
					'Author, A. A. (Year). Title of work [Chart/Graph/Infographic]. Platform/Publisher. Retrieved Date, from URL',
				inTextExample: 'Parenthetical: (Author, Year)\nNarrative: Author (Year)'
			},
			'Radio Broadcast': {
				description:
					'A radio program or broadcast segment, including news reports, interviews, or dramatic performances aired on radio.',
				fields: [
					{
						name: 'contributorsOrganization',
						label: 'Contributor(s)/Organization',
						type: 'list',
						required: true,
						tooltip:
							'Enter the last name and initials of the primary contributor (e.g., Gross, T.). If the broadcast is produced by an organization without highlighting specific individuals, enter the organization\'s name here(e.g., NPR, BBC).'
					},
					{
						name: 'contributorRoles',
						label: 'Contributor Roles',
						type: 'select-list',
						required: true,
						options: [
							'Host',
							'Executive Producer',
							'Producer',
							'Director',
							'Writer',
							'Performer',
							'Reporter',
							'Announcer',
							'Organization (no role)'
						],
						tooltip: 'Select the most appropriate role that describes the contributor\'s primary function.If you entered an Organization name, select "Organization (no role)".'
					},
					{
						name: 'date',
						label: 'Date',
						type: 'date',
						required: true,
						tooltip: 'Enter the original broadcast date in year, month, and day format (e.g., 2024, May 21).'
					},
					{
						name: 'titleOfEpisode',
						label: 'Title of episode',
						type: 'text',
						required: true,
						tooltip: 'Enter the title of the specific episode or segment.'
					},
					{
						name: 'episodeNumber',
						label: 'Episode number',
						type: 'text',
						required: false,
						tooltip:
							'Enter the episode number, if available and relevant.'
					},
					{
						name: 'descriptor',
						label: 'Descriptor [Radio broadcast]',
						type: 'text',
						required: true,
						defaultValue: 'Radio broadcast',
						disabled: true,
						tooltip: 'This field is auto-generated based on your selected source type. WriteForm will display [Radio broadcast] after the title.'
					},
					{
						name: 'titleOfSeries',
						label: 'Title of series',
						type: 'text',
						required: true,
						tooltip: 'Enter the title of the radio program or series in title case, WriteForm will italicize it (e.g., This American Life).'
					},
					{
						name: 'productionCompany',
						label: 'Production company',
						type: 'text',
						required: true,
						tooltip: 'Enter the name of the network or company that produced the broadcast (e.g., NPR, BBC World Service, iHeartMedia).'
					},
					{
						name: 'url',
						label: 'URL',
						type: 'url',
						required: false,
						tooltip:
							'If you accessed the broadcast online, enter the stable URL where it can be streamed or downloaded.'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip: 'Add any internal notes or reminders (e.g., "Interview segment with author," "Cited statistics at 10:30 mark"). Notes are not included in the reference.'
					}
				],
				referenceExample:
					'Host, A. A. (Host). (Year, Month Day). Title of episode [Radio broadcast]. In Title of series. Production Company. URL',
				inTextExample: 'Parenthetical: (Host, Year)\nNarrative: Host (Year)'
			}
		}
	},

	'Government & Institutional Publications': {
		sources: {
			'Press Release': {
				description:
					'An official statement issued by an organization to media outlets and the public.',
				fields: [
					{
						name: 'AuthorOrOrganization',
						label: 'Author or Organization?',
						type: 'radio',
						required: true,
						options: ['Author(s)', 'Organization'],
						tooltip:
							'Enter the name of the organization, company, or government agency that issued the press release (e.g., NASA, Apple Inc., The White House). If an individual author is prominently listed, you may use their name, but the organization is typically the author.'
					},
					{
						name: 'author',
						label: 'Author(s)',
						type: 'list',
						required: true,
						showIf: { field: 'AuthorOrOrganization', value: 'Author(s)' },
						tooltip: 'Enter the issuing person name (initials + last name).'
					},
					{
						name: 'organization',
						label: 'Organization',
						type: 'text',
						required: true,
						showIf: { field: 'AuthorOrOrganization', value: 'Organization' },
						tooltip: 'Issuing organization listed as author.'
					},
					{
						name: 'date',
						label: 'Date',
						type: 'date',
						required: true,
						tooltip: ' Enter the date the press release was issued in year, month, and day format (e.g., 2024, February 15).'
					},
					{
						name: 'titleOfPressRelease',
						label: 'Title of press release',
						type: 'text',
						required: true,
						tooltip: 'Enter the title of the press release exactly as it appears on the source.'
					},
					{
						name: 'descriptor',
						label: 'Descriptor',
						type: 'text',
						required: false,
						disabled: true,
						defaultValue: 'Press release',
						tooltip: 'This field is auto-generated based on your selected source type. WriteForm will display [Press release] after the title.'
					},
					{
						name: 'websitePublisher',
						label: 'Website/Publisher',
						type: 'text',
						required: false,
						tooltip: 'Enter the name of the website or platform where you retrieved the press release (e.g., PR Newswire, Business Wire, or the organization\'s official news page).'
					},
					{
						name: 'url',
						label: 'URL',
						type: 'url',
						required: true,
						tooltip: 'Enter the full, stable web address (URL) linking directly to the press release.'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip:
							'Add any internal notes or reminders (e.g., "Key quote from CEO," "Includes important statistics"). Notes are not included in the reference.'
					}
				],
				referenceExample:
					'Organization Name. (Year, Month Day). Title of press release [Press release]. Website Name (if different). URL\n\nAmerican Psychological Association. (2024, May 10). APA announces new award winners [Press release]. https://www.apa.org/news/press/awards2024',
				inTextExample:
					'Parenthetical: (American Psychological Association, 2024)\nNarrative: American Psychological Association (2024)'
			},
			'Institutional Report': {
				description: 'A report published by an organization, institution, or government agency.',
				fields: [
					{
						name: 'AuthorOrOrganization',
						label: 'Author or Organization?',
						type: 'radio',
						required: true,
						options: ['Author(s)', 'Organization'],
						tooltip:
							'Enter the name of the primary organization responsible for the report (e.g., World Health Organization, U.S. Department of Education). If a specific author is listed, enter their last name followed by initials, but the organization is typically the author.'
					},
					{
						name: 'author',
						label: 'Author(s)',
						type: 'list',
						required: true,
						showIf: { field: 'AuthorOrOrganization', value: 'Author(s)' },
						tooltip: 'Enter the author name (initials + last name).'
					},
					{
						name: 'organization',
						label: 'Organization',
						type: 'text',
						required: true,
						showIf: { field: 'AuthorOrOrganization', value: 'Organization' },
						tooltip: 'Enter the organization that created the report.'
					},
					{
						name: 'year',
						label: 'Year',
						type: 'text',
						required: true,
						tooltip: 'Enter the year the report was published (e.g., 2023). If no date is shown, enter n.d.'
					},
					{
						name: 'titleOfReport',
						label: 'Title of report',
						type: 'text',
						required: true,
						tooltip: 'Enter the title of the report exactly as it appears, WriteForm will italicize it (e.g., Global Trends in Renewable Energy Investment 2023).'
					},
					{
						name: 'reportNumber',
						label: 'Report number',
						type: 'text',
						required: false,
						tooltip:
							'Enter any unique identifying number assigned to the report by the issuing institution (e.g., Contract No. A12345, Report No. 17-2022).'
					},
					{
						name: 'publisher',
						label: 'Publisher',
						type: 'text',
						required: false,
						tooltip:
							'Enter the name of the publishing entity. This is often the same as the authoring organization, but can be a different publishing arm.'
					},
					{
						name: 'doiUrl',
						label: 'DOI/URL',
						type: 'url',
						required: false,
						tooltip:
							'Enter the stable URL or Digital Object Identifier (DOI) that links directly to the report online.'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip:
							'Add any internal notes or reminders (e.g., "Executive summary used for data," "Focus on Chapter 3 findings"). Notes are not included in the reference.'
					}
				],
				referenceExample:
					'Rivera, J. D., & Chen, L. T. (2025). Athlete recovery monitoring study (Report No. 25-111). U.S. Department of Health and Human Services. https://hhs.gov/reports/25-111',
				inTextExample: 'Parenthetical: (Rivera & Chen, 2025)\nNarrative: Rivera and Chen (2025)'
			}
		}
	},

	'Data & Software': {
		sources: {
			'Data set': {
				description:
					'Published or retrievable collection of raw or processed data made available for analysis or replication. Includes datasets stored in institutional repositories, databases, or public archives. (APA §10.9)',
				fields: [
					{
						name: 'AuthorOrOrganization',
						label: 'Author or Organization?',
						type: 'radio',
						required: true,
						options: ['Author(s)', 'Organization'],
						tooltip:
							'Enter the last name and initials of the primary researcher(s) or the name of the organization responsible for creating the data (e.g., Pew Research Center, U.S. Census Bureau).'
					},
					{
						name: 'author',
						label: 'Author(s)',
						type: 'list',
						required: true,
						showIf: { field: 'AuthorOrOrganization', value: 'Author(s)' },
						tooltip: "Enter the author's name (initials + last name)."
					},
					{
						name: 'organization',
						label: 'Organization',
						type: 'text',
						required: true,
						showIf: { field: 'AuthorOrOrganization', value: 'Organization' },
						tooltip:
							'Enter the organization responsible for the dataset (e.g., World Health Organization).'
					},
					{
						name: 'year',
						label: 'Year',
						type: 'text',
						required: true,
						tooltip:
							'Enter the publication year as four digits (e.g., 2023) or n.d. if there is no date.'
					},
					{
						name: 'titleOfDataset',
						label: 'Title of dataset',
						type: 'text',
						required: true,
						tooltip:
							'Enter the title of the data set as it appears in the source (e.g., American Community Survey 2021 Public Use Microdata Sample).'
					},
					{
						name: 'version',
						label: 'Version',
						type: 'text',
						required: false,
						tooltip:
							'Enter the version or edition number of the data set, if applicable (e.g., Version 3.0). This is important for tracking updates.'
					},
					{
						name: 'descriptor',
						label: 'Descriptor',
						type: 'text',
						required: false,
						disabled: true,
						defaultValue: 'Data set',
						tooltip: 'This field is auto-generated based on your selected source type. WriteForm will display [Data set] after the title'
					},
					{
						name: 'publisher',
						label: 'Publisher',
						type: 'text',
						required: false,
						tooltip:
							'Enter the name of the entity that houses or distributes the data set (e.g., Inter-university Consortium for Political and Social Research, figshare). This may be the same as the authoring organization.'
					},
					{
						name: 'retrievalDate',
						label: 'Retrieval date',
						type: 'date',
						required: false,
						tooltip:
							'Enter the date you accessed the data set in year, month, and day format. This is often required for data that is regularly updated or dynamic.'
					},
					{
						name: 'doiUrl',
						label: 'DOI/URL',
						type: 'url',
						required: false,
						tooltip:
							'Enter a DOI or stable URL to the data set if available. Either field may be left blank when your source does not provide one.'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip:
							'Add any internal notes or reminders (e.g., "Used variables X and Y," "Large file size, stored locally"). Notes are not included in the reference.'
					}
				],
				referenceExample:
					'National Center for Education Statistics. (2023). Common Core of Data: Public School Universe Survey, 2021–2022 (Version 1.2) [Data set]. U.S. Department of Education. https://doi.org/10.1234/ccdu.2021.v1.2',
				inTextExample:
					'Parenthetical: (National Center for Education Statistics, 2023)\nNarrative: National Center for Education Statistics (2023)'
			}
		}
	},

	'Course & Education': {
		sources: {
			'Course Handout': {
				description:
					'A document distributed by an instructor for a course, such as class notes, slides, or instructional materials. Includes lecture outlines, reading guides, or other materials shared directly with students but not formally published.',
				fields: [
					{
						name: 'publiclyAccessible',
						label: 'Publicly accessible?',
						type: 'radio',
						required: true,
						options: ['Yes', 'No'],
						tooltip:
							'Select "Yes" if the materials can be viewed by anyone online without needing course login credentials. Select "No" if they are only available through a password- protected learning management system(e.g., Canvas, Blackboard).'
					},
					{
						name: 'presenterOrganization',
						label: 'Presenter/Organization',
						type: 'list',
						required: true,
						tooltip:
							"Enter the last name and initials of the instructor who created the materials (e.g., Wilson, P. R.). If created by a department, enter the organization name (e.g., Biology Department)."
					},
					{
						name: 'date',
						label: 'Date',
						type: 'date',
						required: true,
						tooltip: 'Enter the date the materials were created or used in the course, in year, month, and day format if available (e.g., 2024, Spring Semester).'
					},
					{
						name: 'titleOfPresentation',
						label: 'Title of presentation',
						type: 'text',
						required: true,
						tooltip:
							'Enter the title of the specific handout, slide deck, or set of notes.'
					},
					{
						name: 'format',
						label: 'Format',
						type: 'select',
						required: true,
						options: [
							'Class presentation',
							'Course handout',
							'Instructor materials',
							'Lecture notes',
							'Lecture recording',
							'PowerPoint slides',
							'Syllabus'
						],
						tooltip:
							'Select the option that best describes the type of material you are citing.'
					},
					{
						name: 'course',
						label: 'Course',
						type: 'text',
						required: false,
						tooltip:
							'Enter the course name and number (e.g., PSYCH 101: Introduction to Psychology).'
					},
					{
						name: 'department',
						label: 'Department',
						type: 'text',
						required: false,
						tooltip:
							'Enter the name of the academic department (e.g., Department of Sociology).'
					},
					{
						name: 'institution',
						label: 'Institution',
						type: 'text',
						required: true,
						tooltip:
							'Enter the name of the college, university, or organization (e.g., University of Michigan, Stanford University).'
					},
					{
						name: 'websitePlatformName',
						label: 'Website/Platform',
						type: 'text',
						required: false,
						tooltip:
							'Enter the name of the platform where you accessed the materials (e.g., Canvas, Moodle, course website).'
					},
					{
						name: 'url',
						label: 'URL',
						type: 'url',
						required: true,
						tooltip:
							'If the materials are publicly accessible online, enter the direct web address. For private materials, this is typically left blank.'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip:
							'Add any internal notes or reminders (e.g., "Key diagram on slide 15," "Provided statistical formulas"). Notes are not included in the reference.'
					}
				],
				referenceExample:
					'Author, A. A. (Year). Title of handout [Course handout]. Department or Institution Name.\n\nRivera, J. D. (2024). Leadership theories and practice overview [Course handout]. Department of Educational Leadership, Hampton University.',
				inTextExample: 'Parenthetical: (Rivera, 2024)\nNarrative: Rivera (2024)'
			},
			'Conference Presentation / Session': {
				description:
					'Conference papers, sessions, symposia, panels, posters, keynotes, and workshops presented at professional or academic meetings. Use the descriptor to indicate the format; retrieval (URL or DOI) is required only when the materials are publicly accessible online.',
				fields: [
					{
						name: 'publiclyAccessible',
						label: 'Publicly accessible?',
						type: 'radio',
						required: true,
						options: ['Yes', 'No'],
						tooltip:
							"Select 'Yes' if slides, abstract, video, program, or session details can be viewed without conference-only login. Select 'No' if access is restricted. When not publicly accessible, a URL or DOI is not required."
					},
					{
						name: 'contributorsOrganization',
						label: 'Presenter(s) / contributors / organization',
						type: 'list',
						required: true,
						tooltip:
							'Enter presenters or contributors (e.g., Rodriguez, A. L.) or the sponsoring organization when it is the credited author.'
					},
					{
						name: 'contributorRoles',
						label: 'Contributor role (optional)',
						type: 'select-list',
						required: false,
						options: ['Organization', 'Chair', 'Moderator', 'Organizer', 'Panelist', 'Facilitator'],
						tooltip:
							"If needed, select the role that matches each contributor in order. Leave blank if roles do not apply."
					},
					{
						name: 'date',
						label: 'Date',
						type: 'date',
						required: true,
						tooltip:
							'Enter the presentation or session date, or the conference date range (e.g., 2023, March 22–25).'
					},
					{
						name: 'titleOfPresentation',
						label: 'Title',
						type: 'text',
						required: true,
						tooltip:
							'Enter the title as it appears in the program or materials.'
					},
					{
						name: 'descriptor',
						label: 'Descriptor',
						type: 'select',
						required: true,
						options: [
							'Symposium',
							'Paper presentation',
							'Panel discussion',
							'Poster presentation',
							'Keynote address',
							'Workshop'
						],
						tooltip:
							'Select the format that best describes this source.'
					},
					{
						name: 'nameOfConference',
						label: 'Name of conference',
						type: 'text',
						required: true,
						tooltip:
							'Enter the official conference or meeting name.'
					},
					{
						name: 'locationOfConference',
						label: 'Location of conference',
						type: 'text',
						required: true,
						tooltip:
							'Enter city, state/province, and country, or Virtual / platform as appropriate.'
					},
					{
						name: 'doiUrl',
						label: 'DOI/URL',
						type: 'url',
						required: false,
						tooltip:
							'When publicly accessible, enter the DOI or stable URL to the abstract, slides, recording, poster, or session page. Leave blank when not publicly accessible.'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip:
							'Internal notes only; not shown in the reference list.'
					}
				],
				referenceExample:
					'Presenter, A. A. (Year, Month). Title [Descriptor]. Conference Name, Location. URL or DOI when retrievable.\n\nRivera, J. D. (2025, March). AI-assisted writing in higher education [Conference presentation]. Annual Meeting of the American Educational Research Association, Philadelphia, PA, United States. https://example.org/session',
				inTextExample: 'Parenthetical: (Rivera, 2025)\nNarrative: Rivera (2025)'
			},
			'Open Educational Resource': {
				description:
					'Freely accessible educational materials that are openly licensed for use, adaptation, and sharing.',
				fields: [
					{
						name: 'AuthorOrOrganization',
						label: 'Author or Organization?',
						type: 'radio',
						required: true,
						options: ['Author(s)', 'Organization'],
						tooltip:
							'Allow individual authors or an organization'
					},
					{
						name: 'author',
						label: 'Author(s)',
						type: 'list',
						required: true,
						showIf: { field: 'AuthorOrOrganization', value: 'Author(s)' },
						tooltip: 'Enter the creator name (initials + last name).'
					},
					{
						name: 'organization',
						label: 'Organization',
						type: 'text',
						required: true,
						showIf: { field: 'AuthorOrOrganization', value: 'Organization' },
						tooltip: 'Enter the institution responsible for content.'
					},
					{
						name: 'year',
						label: 'Year',
						type: 'text',
						required: true,
						tooltip: 'Enter the year the resource was published or the most recent update (e.g., 2023).'
					},
					{
						name: 'titleOfOer',
						label: 'Title of OER',
						type: 'text',
						required: true,
						tooltip: 'Enter the full title of the resource,WriteForm will italicize it (e.g., Introduction to Sociology 2e).'
					},
					{
						name: 'edition',
						label: 'Edition',
						type: 'text',
						required: false,
						tooltip:
							'Enter the edition number if applicable (e.g., 2). WriteForm formats it as (2nd ed.) in the reference.'
					},
					{
						name: 'version',
						label: 'Version',
						type: 'text',
						required: false,
						tooltip:
							'Enter the version number if applicable (e.g., 1.3). WriteForm formats it as (Version 1.3) in the reference.'
					},
					{
						name: 'descriptor',
						label: 'Descriptor',
						type: 'select',
						required: false,
						options: [
							'Textbook',
							'Course materials',
							'Learning module',
							'Video',
							'Syllabus',
							'Lecture slides',
							'Other'
						],
						tooltip:
							'Select a category, or choose Other and describe the format below.'
					},
					{
						name: 'descriptorCustom',
						label: 'Custom descriptor',
						type: 'text',
						required: false,
						showIf: { field: 'descriptor', value: 'Other' },
						tooltip: 'Enter a short label for the resource type (e.g., Interactive tutorial).'
					},
					{
						name: 'platformPublisher',
						label: 'Platform/Publisher',
						type: 'text',
						required: true,
						tooltip: 'Enter the name of the repository or website that hosts the OER (e.g., OpenStax, OER Commons, MIT OpenCourseWare).'
					},
					{
						name: 'retrievalDate',
						label: 'Retrieval Date',
						type: 'date',
						required: false,
						tooltip: 'Enter the date you accessed the resource in year, month, and day format. This is recommended for OERs that may be updated over time.'
					},
					{
						name: 'url',
						label: 'URL',
						type: 'url',
						required: true,
						tooltip: 'Enter the stable, direct web address (URL) for the resource.'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip:
							'Add any internal notes or reminders (e.g., "Used Chapter 4," "Excellent diagrams on page 42"). Notes are not included in the reference.'
					}
				],
				referenceExample:
					'Author. (Year). Title of resource (Version) [Descriptor]. Publisher. URL\n\nOpenStax. (2024). Biology 2e (2nd ed.) [Textbook]. OpenStax. https://openstax.org/books/biology-2e',
				inTextExample: 'Parenthetical: (OpenStax, 2024)\nNarrative: OpenStax (2024)'
			},
		}
	},

	'Book & Chapter': {
		sources: {
			Book: {
				description:
					'A stand-alone, authored book published by one or more individuals or an organization. Includes both print and digital editions that are not edited collections or compilations.',
				fields: [
					{
						name: 'authors',
						label: 'Author(s)',
						type: 'list',
						required: true,
						tooltip:
							"Enter each author’s full name (e.g., Stephen King). List all authors in the order they appear on the title page."
					},
					{
						name: 'year',
						label: 'Year',
						type: 'text',
						required: true,
						tooltip:
							'Enter the year of publication for the edition you are using (e.g., 2018).'
					},
					{
						name: 'titleOfBook',
						label: 'Title of book',
						type: 'text',
						required: true,
						tooltip:
							'Enter the full title of the book as it appears on the title page, WriteForm will italicize it (e.g., Educated: A Memoir).'
					},
					{
						name: 'translators',
						label: 'Translator(s)',
						type: 'list',
						required: false,
						tooltip:
							"Enter the translator's full name as it appears on the title page (e.g., Miguel Garcia), one name per field if there are multiple translators."
					},
					{
						name: 'edition',
						label: 'Edition',
						type: 'text',
						required: false,
						tooltip:
							'Enter the edition number if it is not the first edition (e.g., 2nd ed., 4th ed.).'
					},
					{
						name: 'volumes',
						label: 'Volume(s)',
						type: 'text',
						required: false,
						tooltip:
							'If the book is part of a multi-volume set, enter the volume number(s) you are citing (e.g., Vol. 3, or Vols. 1-2).'
					},
					{
						name: 'publisher',
						label: 'Publisher',
						type: 'text',
						required: true,
						tooltip:
							"Enter the name of the publishing company (e.g., Penguin Random House, HarperCollins)."
					},
					{
						name: 'doiUrl',
						label: 'DOI/URL',
						type: 'url',
						required: false,
						tooltip:
							'For online or electronic books, enter the Digital Object Identifier (DOI) or a stable URL where the book can be accessed. For print books, this can be left blank.'
					},
					{
						name: 'originalWorkPublishedYear',
						label: 'Original work published year',
						type: 'text',
						required: false,
						tooltip:
							'For translated books, include the original publication year if known. APA Style strongly recommends this for proper citation (e.g., 1869).'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip: 'Add any internal notes or reminders (e.g., "Cited Chapter 5," "Used for theoretical framework"). Notes are not included in the reference.'
					}
				],
				referenceExample:
					'Author, A. A. (Year). <em>Title of book</em> (Edition if applicable, Vols. x–x if any). Publisher. https://doi.org/xxxxx\n\nHill, C. A. (2023). <em>Developing mental toughness in athletes</em> (2nd ed.). SportEdge Press. https://doi.org/10.4324/sportedge.mt2023',
				inTextExample: 'Parenthetical: (Hill, 2023)\nNarrative: Hill (2023)'
			},
			'Chapter in Edited Book': {
				description:
					"A single chapter or section written by one or more authors within an edited volume. The reference lists both the chapter authors and the book's editors, with page numbers for the specific chapter.",
				fields: [
					{
						name: 'chapterAuthors',
						label: 'Chapter author(s)',
						type: 'list',
						required: true,
						tooltip:
							"Enter each chapter author's full name (e.g., Stephen King). List all chapter authors in the order they appear on the title page."
					},
					{
						name: 'year',
						label: 'Year',
						type: 'text',
						required: true,
						tooltip:
							'Enter the publication year of the book edition you are using (e.g., 2020).'
					},
					{
						name: 'titleOfChapter',
						label: 'Title of chapter',
						type: 'text',
						required: true,
						tooltip:
							'Enter the title of the specific chapter or essay. Do not italicize or use quotation marks in this field.'
					},
					{
						name: 'editors',
						label: 'Editor(s)',
						type: 'list',
						required: false,
						tooltip:
							"Enter each editor's full name (e.g., Stephen King). List all editors in the order they appear on the title page."
					},
					{
						name: 'titleOfBook',
						label: 'Title of book',
						type: 'text',
						required: true,
						tooltip:
							'Enter the full title of the entire edited book, in sentence case (e.g., The Psychology of Social Media).'
					},
					{
						name: 'translators',
						label: 'Translator(s)',
						type: 'list',
						required: false,
						tooltip:
							"Enter the translator's full name as it appears on the title page (e.g., Miguel Garcia), one name per field if there are multiple translators."
					},
					{
						name: 'edition',
						label: 'Edition',
						type: 'text',
						required: false,
						tooltip:
							'Enter the edition number if it is not the first edition (e.g., 3rd ed.).'
					},
					{
						name: 'volumes',
						label: 'Volume(s)',
						type: 'text',
						required: false,
						tooltip:
							'If the book is part of a multi-volume set, enter the volume number(s) you are citing (e.g., Vol. 2).'
					},
					{
						name: 'pageRangeStart',
						label: 'Page range - Start',
						type: 'text',
						required: true,
						width: '50%',
						tooltip: 'Enter the starting page number of the chapter (e.g., 15).'
					},
					{
						name: 'pageRangeEnd',
						label: 'Page range - End',
						type: 'text',
						required: true,
						width: '50%',
						tooltip: 'Enter the ending page number of the chapter (e.g., 42).'
					},
					{
						name: 'publisher',
						label: 'Publisher',
						type: 'text',
						required: true,
						tooltip:
							"Enter the name of the publishing company (e.g., Oxford University Press)."
					},
					{
						name: 'doiUrl',
						label: 'DOI/URL',
						type: 'url',
						required: false,
						tooltip:
							'For online or electronic books, enter the Digital Object Identifier (DOI) or a stable URL. For print books, this can be left blank.'
					},
					{
						name: 'originalWorkPublishedYear',
						label: 'Original work published year',
						type: 'text',
						required: false,
						tooltip:
							'Optional. If this chapter is a translation, enter the original publication year when known (e.g., 1995).'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip: 'Add any internal notes or reminders (e.g., "Key theory introduced here," "Used in literature review"). Notes are not included in the reference.'
					}
				],
				referenceExample:
					'Chapter Author, A. A. (Year). Title of chapter. In E. E. Editor (Ed.), <em>Title of book</em> (pp. xx–xx). Publisher. https://doi.org/xxxxx\n\nHill, B., & Hill, C. (2025). Traveling abroad. In J. Anglin (Ed.), <em>How to live in Europe</em> (2nd ed., Vol. 12, pp. 22–42). Trindale Publishing. https://travelingabroad.com',
				inTextExample:
					'Parenthetical: (Hill & Hill, 2025)\nNarrative: Hill and Hill (2025)'
			},
			'Reference Work Entry': {
				description:
					'An entry, article, or definition within a reference work such as a dictionary, encyclopedia, or handbook. The entry may come from a print or online source and typically lists an editor or institutional author for the overall work.',
				fields: [
					{
						name: 'AuthorOrOrganization',
						label: 'Author or Organization?',
						type: 'radio',
						required: true,
						options: ['Author(s)', 'Organization'],
						tooltip:
							'Select whether this entry is credited to individual author(s) or to an organization.'
					},
					{
						name: 'entryAuthors',
						label: 'Entry author(s)',
						type: 'list',
						required: true,
						showIf: { field: 'AuthorOrOrganization', value: 'Author(s)' },
						tooltip:
							"Enter the last name and initials of the author(s) of the specific entry, if listed (e.g., Gupta, R. K.). If no author is given, the entry title will begin with the citation."
					},
					{
						name: 'organization',
						label: 'Organization',
						type: 'text',
						required: true,
						showIf: { field: 'AuthorOrOrganization', value: 'Organization' },
						tooltip:
							'Enter the full name of the organization responsible for the entry (e.g., American Psychological Association).'
					},
					{
						name: 'year',
						label: 'Year',
						type: 'text',
						required: true,
						tooltip:
							'Enter the publication year of the reference work edition you are using (e.g., 2023). If not listed, enter n.d. (for “no date”). If continuously updated, include a retrieval date.'
					},
					{
						name: 'titleOfEntry',
						label: 'Title of entry',
						type: 'text',
						required: true,
						tooltip:
							'Enter the title of the specific entry or article. Do not italicize or use quotation marks in this field.'
					},
					// {
					// 	name: 'entryEditors',
					// 	label: 'Editor(s) of entry',
					// 	type: 'list',
					// 	required: false,
					// 	tooltip:
					// 		'If the entry has its own editor(s) separate from the overall work, enter their names here.'
					// },
					{
						name: 'referenceWorkEditors',
						label: 'Editor(s) of reference work',
						type: 'list',
						required: false,
						tooltip:
							'Enter each editor\'s last name followed by initials, WriteForm will format it correctly in the Reference entry(e.g., Johnson, P.K.).'
					},
					{
						name: 'titleOfReferenceWork',
						label: 'Title of reference work',
						type: 'text',
						required: true,
						tooltip:
							'Enter the full title of the entire reference work, WriteForm will italicize it (e.g., Encyclopedia of Cognitive Science).'
					},
					{
						name: 'edition',
						label: 'Edition',
						type: 'text',
						required: false,
						tooltip: 'Enter the edition number if it is not the first edition (e.g., 5th ed.).'
					},
					{
						name: 'volumes',
						label: 'Volume(s)',
						type: 'text',
						required: false,
						tooltip:
							'If the reference work is multi-volume, enter the volume number(s) you are citing (e.g., Vol. 4).'
					},
					{
						name: 'pageRangeStart',
						label: 'Page range - Start',
						type: 'text',
						required: false,
						width: '50%',
						tooltip:
							'Enter the starting page number if the entry spans multiple pages (e.g., 45). Leave blank for single-page entries.'
					},
					{
						name: 'pageRangeEnd',
						label: 'Page range - End',
						type: 'text',
						required: false,
						width: '50%',
						tooltip:
							'Enter the ending page number if the entry spans multiple pages (e.g., 52). Leave blank for single-page entries.'
					},
					{
						name: 'publisher',
						label: 'Publisher',
						type: 'text',
						required: true,
						tooltip:
							"Enter the name of the publishing company (e.g., Oxford University Press, Sage Publications)."
					},
					{
						name: 'retrievalDate',
						label: 'Retrieval date',
						type: 'date',
						required: false,
						tooltip:
							'Enter the date you accessed the entry in year, month, and day format. This is required for online reference works that are continuously updated (e.g., Wikipedia, online dictionaries).'
					},
					{
						name: 'doiUrl',
						label: 'DOI/URL',
						type: 'url',
						required: false,
						tooltip:
							'For online reference works, enter the DOI or stable URL. For print works, leave blank and provide the page range instead.'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip: 'Add any internal notes or reminders (e.g., "Excellent summary of the concept," "Includes key historical timeline"). Notes are not included in the reference.'
					}
				],
				referenceExample:
					'Author, A. A. (Year). Title of entry. In E. E. Editor (Ed.), Title of reference work (Edition, Vol. #, pp. xx–xx). Publisher. https://doi.org/xxxxx',
				inTextExample: 'Parenthetical: (Author, Year)\nNarrative: Author (Year)'
			},
			'Thesis/Dissertation': {
				description:
					'An academic research project formally submitted to a university in fulfillment of a degree requirement. It may be retrieved from a database (e.g., ProQuest), university repository, or publicly accessible archive.',
				fields: [
					{
						name: 'authors',
						label: 'Author(s)',
						type: 'list',
						required: true,
						tooltip:
							"Enter the author's last name followed by initials (e.g., Washington, G. L.)."
					},
					{
						name: 'year',
						label: 'Year',
						type: 'text',
						required: true,
						tooltip:
							'Enter the year the thesis or dissertation was published or submitted (e.g., 2023).'
					},
					{
						name: 'titleOfThesisDissertation',
						label: 'Title of thesis/dissertation',
						type: 'text',
						required: true,
						tooltip:
							'Enter the full title of the work, WriteForm will italicize it (e.g., The Impact of Microplastics on Freshwater Ecosystems: A Three-Year Study).'
					},
					{
						name: 'publicationNumberAccessionId',
						label: 'Publication number/Accession ID',
						type: 'text',
						required: false,
						tooltip: 'Enter any unique identifier assigned by the database or repository (e.g., ProQuest order number, database accession number).'
					},
					{
						name: 'descriptor',
						label: 'Descriptor',
						type: 'select',
						required: true,
						options: [
							'Doctoral dissertation',
							"Master's thesis",
							'Undergraduate honors thesis'
						],
						tooltip:
							'Select the option that describes the type and level of the academic work.'
					},
					{
						name: 'publicationStatus',
						label: 'Publication status',
						type: 'select',
						required: true,
						options: ['Database', 'Online (not in database)', 'Unpublished', 'Print only'],
						tooltip:
							'Select where this thesis/dissertation is available: in a database, online (not in a database), unpublished, or print only.'
					},
					{
						name: 'institution',
						label: 'Institution',
						type: 'text',
						required: true,
						tooltip:
							'Enter the name of the college or university that granted the degree (e.g., University of Toronto, Stanford University).'
					},
					{
						name: 'publisherRepository',
						label: 'Publisher/Repository',
						type: 'text',
						required: false,
						tooltip:
							'Required when publication status is Database: enter the database or repository name (e.g., ProQuest Dissertations and Theses). Leave blank for unpublished works or when the work is only online at the institution.'
					},
					{
						name: 'doiUrl',
						label: 'DOI/URL',
						type: 'url',
						required: false,
						tooltip:
							'Optional. Enter a DOI or URL when the thesis is available online or in a database. Not required for print-only or unpublished works.'
					},
					{
						name: 'notes',
						label: 'Notes',
						type: 'textarea',
						required: false,
						tooltip: 'Add any internal notes or reminders (e.g., "Excellent literature review," "Key findings in Chapter 4"). Notes are not included in the reference.'
					}
				],
				referenceExample:
					'Author, A. A. (Year). Title of dissertation or thesis [Descriptor, Name of Institution]. Publisher/Database or Repository. https://doi.org/xxxxx',
				inTextExample: 'Parenthetical: (Author, Year)\nNarrative: Author (Year)'
			}
		}
	}
};

// Helper function to get sources for a category
export function getSourcesForCategory(category) {
	const categoryConfig = citationConfig[category];
	if (!categoryConfig) return [];

	// If there's an explicit source order defined, use it
	if (sourceOrder[category]) {
		return sourceOrder[category].filter((source) => categoryConfig.sources[source]);
	}

	// Otherwise fall back to Object.keys order
	return Object.keys(categoryConfig.sources);
}

function injectGroupAuthorAbbreviationField(fields) {
	if (!fields.some((f) => f.name === 'AuthorOrOrganization')) return fields;
	if (!fields.some((f) => f.name === 'organization')) return fields;
	if (fields.some((f) => f.name === 'groupAuthorAbbreviation')) return fields;
	const orgIdx = fields.findIndex((f) => f.name === 'organization');
	if (orgIdx === -1) return fields;
	const inject = {
		name: 'groupAuthorAbbreviation',
		label: 'Group author abbreviation',
		type: 'text',
		required: false,
		showIf: { field: 'AuthorOrOrganization', value: 'Organization' },
		tooltip:
			'Optional. APA 7: uppercase letters only, no spaces (e.g., NIMH). First in-text citation shows the full organization name with this abbreviation; later citations use the abbreviation alone.'
	};
	return [...fields.slice(0, orgIdx + 1), inject, ...fields.slice(orgIdx + 1)];
}

// Helper function to get field configuration for a source
export function getFieldsForSource(category, source) {
	const categoryConfig = citationConfig[category];
	if (!categoryConfig || !categoryConfig.sources[source]) return [];
	const fields = categoryConfig.sources[source].fields;
	return injectGroupAuthorAbbreviationField(fields);
}

// Helper function to get source description
export function getSourceDescription(category, source) {
	const categoryConfig = citationConfig[category];
	if (!categoryConfig || !categoryConfig.sources[source]) return '';
	return categoryConfig.sources[source].description;
}

// Helper function to get reference example
export function getReferenceExample(category, source) {
	const categoryConfig = citationConfig[category];
	if (!categoryConfig || !categoryConfig.sources[source]) return '';
	return categoryConfig.sources[source].referenceExample;
}

// Helper function to get in-text example
export function getInTextExample(category, source) {
	const categoryConfig = citationConfig[category];
	if (!categoryConfig || !categoryConfig.sources[source]) return '';
	return categoryConfig.sources[source].inTextExample;
}
